import React from 'react'
import { useStateWithDebounce, useUserLogs, SortDirectionEnum } from '@modmd/data'
import { TableLabel, TableToRoute, useSortChangeHandler } from 'utils/helpers'
import { Box, Container } from 'components/Layout'
import { SelectInput } from 'components/SelectInput'
import { DateInput } from 'components/DateInput'
import TextInput from 'components/TextInput'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { RESULTS_PER_PAGE } from 'internal-portal/constants/filters'
import { endOfDay, startOfDay } from 'date-fns'
import { UserLogsDatalist } from 'sharedComponents/UserLogsDatalist'

export enum FormName {
  DATE_FROM = 'dateFrom',
  DATE_TO = 'dateTo',
  USER = 'userNameOrEmail',
  TABLE = 'table',
  ID = 'id',
  COMPANY_GROUP = 'companyGroup',
  RESULTS_PER_PAGE = 'resultsPerPage',
}

export enum PaginationProps {
  PAGE = 'page',
}

const UserLogs = () => {
  const searchState = useSearchState<{
    [FormName.DATE_TO]?: string
    [FormName.DATE_FROM]?: string
    [FormName.USER]?: string
    [FormName.ID]?: string
    [FormName.TABLE]?: string
    [PaginationProps.PAGE]?: string
    [FormName.RESULTS_PER_PAGE]?: string
  }>()
  const { page } = searchState.data
  const [userNameOrEmail, setUserNameOrEmail, debouncedUserNameOrEmail] = useStateWithDebounce(
    '',
    (userNameOrEmail) => {
      searchState.setSearchState({ userNameOrEmail, page: 1 }, false, true)
    }
  )

  const [idLike, setIdLike, debouncedIdLike] = useStateWithDebounce('', (idLike) => {
    searchState.setSearchState({ idLike, page: 1 }, false, true)
  })

  const [companyGroupLike, setCompanyGroupLike, debouncedCompanyGroupLike] = useStateWithDebounce(
    '',
    (companyGroupLike) => {
      searchState.setSearchState({ companyGroupLike, page: 1 }, false, true)
    }
  )

  const { handleSortChange, sorter } = useSortChangeHandler()
  const { data: userLogsData, loading: isLoading } = useUserLogs({
    filter: Object.entries({
      emailOrNameLike: debouncedUserNameOrEmail,
      table: searchState.data.table,
      idLike: debouncedIdLike,
      companyGroupLike: debouncedCompanyGroupLike,
      dateFrom: searchState.data.dateFrom,
      dateTo: searchState.data.dateTo,
    }).reduce((acc, [key, value]) => (value ? { ...acc, [key]: value } : acc), {}),
    page: Number(page || 1),
    direction: SortDirectionEnum.DESC,
    pageLength:
      Number(searchState.data[FormName.RESULTS_PER_PAGE]) || Number(RESULTS_PER_PAGE[0].value),
  })

  const handleFilterChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      searchState.setSearchState({ [name]: value, page: 1 }, false, true)
    },
    [searchState]
  )

  return (
    <DashboardPage>
      <Container>
        <UserLogsDatalist
          tableHeader={
            <Box
              display="grid"
              gridTemplateColumns={{
                TABLET: 'repeat(2, 1fr)',
                DESKTOP: 'repeat(5, 1fr)',
              }}
              gridGap="1rem"
            >
              <DateInput
                id="dateFrom"
                name={FormName.DATE_FROM}
                label="Date From"
                value={
                  searchState.data.dateFrom
                    ? new Date(searchState.data[FormName.DATE_FROM] as string)
                    : null
                }
                onChange={(date) => {
                  searchState.setSearchState(
                    {
                      [FormName.DATE_FROM]: date ? startOfDay(date).toISOString() : null,
                      page: 1,
                    },
                    false,
                    true
                  )
                }}
                isFullWidth
                hasBottomContent
              />
              <DateInput
                id="dateTo"
                name={FormName.DATE_TO}
                label="Date To"
                value={
                  searchState.data.dateTo
                    ? new Date(searchState.data[FormName.DATE_TO] as string)
                    : null
                }
                onChange={(date) => {
                  searchState.setSearchState(
                    {
                      [FormName.DATE_TO]: date ? endOfDay(date).toISOString() : null,
                      page: 1,
                    },
                    false,
                    true
                  )
                }}
                isFullWidth
              />
              <TextInput
                name={FormName.USER}
                label="User"
                placeholder="Name or email"
                value={userNameOrEmail}
                onChange={(event) => setUserNameOrEmail(event.target.value)}
                hasBottomContent
              />
              <TextInput
                name={FormName.ID}
                label="ID"
                placeholder="ID"
                value={idLike}
                onChange={(event) => setIdLike(event.target.value)}
                hasBottomContent
              />
              <TextInput
                name={FormName.COMPANY_GROUP}
                label="Company or Group"
                placeholder="Company or Group"
                value={companyGroupLike}
                onChange={(event) => setCompanyGroupLike(event.target.value)}
                hasBottomContent
              />
              <SelectInput
                name={FormName.TABLE}
                label="Section"
                value={searchState.data[FormName.TABLE] || ''}
                onChange={handleFilterChange}
                options={[
                  {
                    value: '',
                    label: 'All',
                  },
                  ...Object.keys(TableToRoute).map((table) => ({
                    value: table,
                    label: TableLabel[table as keyof typeof TableLabel],
                  })),
                ]}
                hasBottomContent
              />
              <SelectInput
                name={FormName.RESULTS_PER_PAGE}
                label="Results per page"
                value={searchState.data[FormName.RESULTS_PER_PAGE] || ''}
                onChange={handleFilterChange}
                options={RESULTS_PER_PAGE}
                hasBottomContent
              />
            </Box>
          }
          userLogs={userLogsData?.getUserLogs?.userLogs || []}
          pagination={userLogsData?.getUserLogs?.pagination}
          isLoading={isLoading}
          onSortChange={handleSortChange}
          sorter={sorter}
          onPageChange={(page) => searchState.setSearchState({ page }, false)}
          pageLength={userLogsData?.getUserLogs?.pagination.length}
        />
      </Container>
    </DashboardPage>
  )
}

export default UserLogs
